import { randomBase64Url, safeFileName, type FileOffer } from "@kitae/transfer-protocol";

export interface PreparedFile {
  readonly file: File;
  readonly offer: FileOffer;
}

async function imageThumbnail(file: File): Promise<string | undefined> {
  if (!file.type.startsWith("image/") || file.type === "image/svg+xml") return undefined;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 256 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const thumbnail = canvas.toDataURL("image/jpeg", 0.72);
    return thumbnail.length <= 96_000 ? thumbnail : undefined;
  } catch {
    return undefined;
  }
}

export async function prepareFiles(files: readonly File[], onProgress?: (done: number, total: number) => void): Promise<PreparedFile[]> {
  const prepared: PreparedFile[] = [];
  let done = 0;
  for (const file of files) {
    const offer: FileOffer = {
      id: randomBase64Url(12),
      name: safeFileName(file.name),
      size: file.size,
      type: file.type || "application/octet-stream",
      lastModified: file.lastModified,
      thumbnail: await imageThumbnail(file),
    };
    prepared.push({ file, offer });
    done += 1;
    onProgress?.(done, files.length);
  }
  return prepared;
}

export function fileKind(offer: FileOffer): string {
  const extension = offer.name.includes(".") ? offer.name.split(".").pop()?.toUpperCase() : undefined;
  return extension || offer.type.split("/").pop()?.toUpperCase() || "FICHIER";
}
